<template>
  <div class="engineers-page">
    <!-- ============== HERO / HEADER ============== -->
    <div class="page-hero">
      <div class="page-hero__bg">
        <div class="bubble bubble-1" />
        <div class="bubble bubble-2" />
        <div class="bubble bubble-3" />
      </div>

      <div class="page-hero__content">
        <div class="page-hero__title-block">
          <div class="page-hero__icon">
            <feather-icon
              icon="UsersIcon"
              size="28"
            />
          </div>
          <div>
            <h2 class="page-hero__title">
              Engineers Directory
            </h2>
            <p class="page-hero__subtitle">
              Manage, track and explore your engineering team in real time
            </p>
          </div>
        </div>

        <!-- Stat cards -->
        <div class="stats-row">
          <div class="stat-card stat-card--primary">
            <div class="stat-card__icon">
              <feather-icon
                icon="UsersIcon"
                size="20"
              />
            </div>
            <div class="stat-card__body">
              <span class="stat-card__label">Total Engineers</span>
              <span class="stat-card__value">{{ totalRows || 0 }}</span>
            </div>
          </div>

          <div class="stat-card stat-card--success">
            <div class="stat-card__icon">
              <feather-icon
                icon="ActivityIcon"
                size="20"
              />
            </div>
            <div class="stat-card__body">
              <span class="stat-card__label">Active Load</span>
              <span class="stat-card__value">{{ totalActiveLoad }}</span>
            </div>
          </div>

          <div class="stat-card stat-card--warning">
            <div class="stat-card__icon">
              <feather-icon
                icon="BriefcaseIcon"
                size="20"
              />
            </div>
            <div class="stat-card__body">
              <span class="stat-card__label">Departments</span>
              <span class="stat-card__value">{{ uniqueDepartments }}</span>
            </div>
          </div>

          <div class="stat-card stat-card--info">
            <div class="stat-card__icon">
              <feather-icon
                icon="GlobeIcon"
                size="20"
              />
            </div>
            <div class="stat-card__body">
              <span class="stat-card__label">Offices</span>
              <span class="stat-card__value">{{ uniqueOffices }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============== TOOLBAR ============== -->
    <div class="toolbar">
      <div class="toolbar__search">
        <div class="toolbar__search-field">
          <feather-icon
            icon="SearchIcon"
            size="18"
            class="toolbar__search-icon"
          />
          <input
            v-model="searchTerm"
            type="text"
            class="toolbar__search-input"
            placeholder="Search engineer by name, email or job..."
            @keyup.enter="searchNow"
          >
          <button
            v-if="searchTerm"
            class="toolbar__clear-btn"
            @click="clearSearch"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>

      </div>

      <div class="toolbar__filters">
        <div class="toolbar__selects">
          <div class="custom-select">
            <feather-icon
              icon="BriefcaseIcon"
              size="14"
              class="custom-select__icon"
            />
            <select
              v-model="selectedDepartment"
              @change="onFilterChange"
            >
              <option value="">
                All Departments
              </option>
              <option
                v-for="dep in departmentsList"
                :key="dep.value"
                :value="dep.value"
              >
                {{ dep.text }}
              </option>
            </select>
          </div>

          <div class="custom-select">
            <feather-icon
              icon="MapPinIcon"
              size="14"
              class="custom-select__icon"
            />
            <select
              v-model="selectedOffice"
              @change="onFilterChange"
            >
              <option value="">
                All Offices
              </option>
              <option
                v-for="off in officesList"
                :key="off.value"
                :value="off.value"
              >
                {{ off.text }}
              </option>
            </select>
          </div>
        </div>
        <div class="view-toggle">
          <button
            :class="['view-toggle__btn', { active: viewMode === 'grid' }]"
            title="Grid view"
            @click="viewMode = 'grid'"
          >
            <feather-icon
              icon="GridIcon"
              size="16"
            />
          </button>
          <button
            :class="['view-toggle__btn', { active: viewMode === 'list' }]"
            title="List view"
            @click="viewMode = 'list'"
          >
            <feather-icon
              icon="ListIcon"
              size="16"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- ============== LOADING ============== -->
    <div
      v-if="dataLoad"
      class="state-block"
    >
      <div class="loader-pulse">
        <div class="loader-pulse__circle" />
        <div class="loader-pulse__circle" />
        <div class="loader-pulse__circle" />
      </div>
      <p class="state-block__text">
        Loading engineers...
      </p>
    </div>

    <!-- ============== EMPTY STATE ============== -->
    <div
      v-else-if="rows.length === 0"
      class="state-block"
    >
      <div class="empty-illustration">
        <feather-icon
          icon="UsersIcon"
          size="48"
        />
      </div>
      <h4 class="state-block__title">
        No Engineers Found
      </h4>
      <p class="state-block__text">
        Try adjusting your search or filters.
      </p>
    </div>

    <!-- ============== GRID VIEW ============== -->
    <div
      v-else-if="viewMode === 'grid'"
      class="engineers-grid"
    >
      <div
        v-for="(engineer, idx) in rows"
        :key="engineer.id"
        class="engineer-card"
        :style="{ animationDelay: `${idx * 40}ms` }"
        @click="handleRowClick(engineer)"
      >
        <!-- Top accent strip -->
        <div :class="['engineer-card__accent', loadAccentClass(engineer)]" />

        <!-- Photo + status -->
        <div class="engineer-card__top">
          <div class="engineer-avatar-wrapper">
            <div class="engineer-avatar-ring">
              <img
                v-if="engineer.photo"
                :src="engineer.photo"
                :alt="getNameWithoutLoad(engineer.name)"
                class="engineer-avatar-img"
                @error="onImgError($event)"
              >
              <div
                v-else
                class="engineer-avatar-fallback"
              >
                {{ getInitials(engineer.name) }}
              </div>
            </div>
            <span
              :class="['engineer-status-dot', engineer.busyFrom ? 'busy' : 'available']"
              :title="engineer.busyFrom ? 'Busy' : 'Available'"
            />
          </div>

          <button
            class="engineer-card__training-btn"
            title="Training records"
            @click.stop="goToTraining(engineer.id)"
          >
            <feather-icon
              icon="AwardIcon"
              size="16"
            />
            <span
              v-if="engineer.training_attendees_count"
              class="engineer-card__training-badge"
            >{{ engineer.training_attendees_count }}</span>
          </button>
        </div>

        <!-- Name & job -->
        <div class="engineer-card__body">
          <h5 class="engineer-card__name">
            {{ getNameWithoutLoad(engineer.name) }}
          </h5>
          <p class="engineer-card__job">
            <feather-icon
              icon="BriefcaseIcon"
              size="12"
              class="mr-25"
            />
            {{ engineer.job_description || '—' }}
          </p>

          <!-- Department + office chips -->
          <div class="engineer-card__chips">
            <span
              v-if="engineer.department_name"
              class="chip chip--dep"
            >
              <feather-icon
                icon="LayersIcon"
                size="11"
                class="mr-25"
              />
              {{ engineer.department_name }}
            </span>
            <span
              v-if="engineer.office && engineer.office.name"
              class="chip chip--office"
            >
              <feather-icon
                icon="MapPinIcon"
                size="11"
                class="mr-25"
              />
              {{ engineer.office.country || engineer.office.name }}
            </span>
          </div>

          <!-- Email -->
          <div
            v-if="engineer.email"
            class="engineer-card__contact"
          >
            <feather-icon
              icon="MailIcon"
              size="12"
              class="mr-25"
            />
            <span class="text-truncate">{{ engineer.email }}</span>
          </div>

          <!-- Load progress -->
          <div class="engineer-card__loads">
            <div
              v-for="(item, i) in formatLoad(engineer.load)"
              :key="i"
              class="load-item"
            >
              <div class="load-item__head">
                <span class="load-item__label">{{ item.label }}</span>
                <span class="load-item__count">{{ item.count }}</span>
              </div>
              <div class="load-item__bar">
                <div
                  class="load-item__bar-fill"
                  :style="{ width: loadPercent(item.count) + '%' }"
                  :class="loadFillClass(item.count)"
                />
              </div>
            </div>
          </div>

          <!-- Footer info -->
          <div class="engineer-card__footer">
            <div class="engineer-card__footer-item">
              <feather-icon
                icon="CalendarIcon"
                size="12"
              />
              <span>{{ formatDate(engineer.joined_at) }}</span>
            </div>
            <div
              v-if="engineer.certificates_groups && engineer.certificates_groups.length"
              class="engineer-card__footer-item"
            >
              <feather-icon
                icon="ShieldIcon"
                size="12"
              />
              <span>{{ engineer.certificates_groups.length }} groups</span>
            </div>
          </div>

          <!-- Action steps -->
          <div class="engineer-card__actions">
            <button
              class="action-btn action-btn--view"
              title="View details"
              @click.stop="handleRowClick(engineer)"
            >
              <feather-icon
                icon="EyeIcon"
                size="14"
              />
              <span>View</span>
            </button>
            <button
              v-if="canViewCertificates"
              class="action-btn action-btn--cert"
              title="Certificates status"
              @click.stop="goToCertificates(engineer.id)"
            >
              <feather-icon
                icon="ShieldIcon"
                size="14"
              />
              <span>Certificates</span>
            </button>
            <button
              class="action-btn action-btn--training"
              title="Training"
              @click.stop="goToTraining(engineer.id)"
            >
              <feather-icon
                icon="AwardIcon"
                size="14"
              />
              <span>Training</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============== LIST VIEW ============== -->
    <div
      v-else
      class="engineers-list"
    >
      <div
        v-for="(engineer, idx) in rows"
        :key="engineer.id"
        class="engineer-row"
        :style="{ animationDelay: `${idx * 25}ms` }"
        @click="handleRowClick(engineer)"
      >
        <div class="engineer-row__left">
          <div class="engineer-avatar-wrapper engineer-avatar-wrapper--sm">
            <div class="engineer-avatar-ring engineer-avatar-ring--sm">
              <img
                v-if="engineer.photo"
                :src="engineer.photo"
                class="engineer-avatar-img"
                :alt="getNameWithoutLoad(engineer.name)"
                @error="onImgError($event)"
              >
              <div
                v-else
                class="engineer-avatar-fallback"
              >
                {{ getInitials(engineer.name) }}
              </div>
            </div>
            <span
              :class="['engineer-status-dot', engineer.busyFrom ? 'busy' : 'available']"
            />
          </div>
          <div class="engineer-row__main">
            <h6 class="engineer-row__name">
              {{ getNameWithoutLoad(engineer.name) }}
            </h6>
            <p class="engineer-row__sub">
              {{ engineer.job_description || '—' }}
              <span
                v-if="engineer.department_name"
                class="dot-sep"
              >•</span>
              {{ engineer.department_name }}
            </p>
          </div>
        </div>

        <div class="engineer-row__loads">
          <div
            v-for="(item, i) in formatLoad(engineer.load)"
            :key="i"
            class="load-pill"
            :class="loadFillClass(item.count)"
          >
            <feather-icon
              icon="UsersIcon"
              size="11"
              class="mr-25"
            />
            <strong>{{ item.count }}</strong>
            <span class="load-pill__label">{{ item.label }}</span>
          </div>
        </div>

        <div class="engineer-row__office">
          <feather-icon
            icon="MapPinIcon"
            size="12"
            class="mr-25"
          />
          {{ (engineer.office && (engineer.office.country || engineer.office.name)) || '—' }}
        </div>

        <div class="engineer-row__actions">
          <button
            class="icon-btn"
            title="Training"
            @click.stop="goToTraining(engineer.id)"
          >
            <feather-icon
              icon="AwardIcon"
              size="14"
            />
          </button>
          <button
            v-if="canViewCertificates"
            class="icon-btn"
            title="Certificates"
            @click.stop="goToCertificates(engineer.id)"
          >
            <feather-icon
              icon="ShieldIcon"
              size="14"
            />
          </button>
          <button
            class="icon-btn icon-btn--primary"
            title="View"
            @click.stop="handleRowClick(engineer)"
          >
            <feather-icon
              icon="ChevronRightIcon"
              size="14"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- ============== PAGINATION ============== -->
    <div
      v-if="!dataLoad && totalRows > perPage"
      class="pagination-wrapper"
    >
      <div class="pagination-wrapper__info">
        Showing
        <span class="font-weight-bold">{{ (page - 1) * perPage + 1 }}</span>
        -
        <span class="font-weight-bold">{{ Math.min(page * perPage, totalRows) }}</span>
        of
        <span class="font-weight-bold">{{ totalRows }}</span>
      </div>
      <b-pagination
        v-model="page"
        :total-rows="totalRows"
        :per-page="perPage"
        align="end"
        first-number
        last-number
        :total-visible="6"
        class="mb-0 custom-pagination"
        @input="getAllData"
      >
        <template #prev-text>
          <feather-icon
            icon="ChevronLeftIcon"
            size="16"
          />
        </template>
        <template #next-text>
          <feather-icon
            icon="ChevronRightIcon"
            size="16"
          />
        </template>
      </b-pagination>
    </div>

    <!-- ============== RIGHT SIDEBAR (UNCHANGED) ============== -->
    <right-sidebar
      v-model="isSidebarOpen"
      :title="selectedEngineer ? `Engineer: ${selectedEngineer.name}` : 'Engineer Details'"
      width="600px"
      :show-tabs="true"
      :tabs="[
        { id: 'engineer-info', label: 'Engineer Information' },
        { id: 'status-office', label: 'Status & Office' },
        { id: 'certificates', label: 'Certificates Groups' },
      ]"
      @hide="handleSidebarClose"
    >
      <template #engineer-info>
        <engineers-info
          v-if="selectedEngineer"
          :engineer="engineerDetails || selectedEngineer"
          :loading="engineerLoading"
          section="engineer-info"
        />
      </template>
      <template #status-office>
        <engineers-info
          v-if="selectedEngineer"
          :engineer="engineerDetails || selectedEngineer"
          :loading="engineerLoading"
          section="status-office"
        />
      </template>
      <template #certificates>
        <engineers-info
          v-if="selectedEngineer"
          :engineer="engineerDetails || selectedEngineer"
          :loading="engineerLoading"
          section="certificates"
        />
      </template>
    </right-sidebar>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import { BPagination } from 'bootstrap-vue'
import RightSidebar from '@/components/RightSidebar.vue'
import EngineersInfo from './control-engineers/EngineersInfo.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  components: {
    BPagination,
    RightSidebar,
    EngineersInfo,
  },
  data() {
    return {
      rows: [],
      totalRows: 0,
      page: 1,
      perPage: 20,
      dataLoad: false,
      searchTerm: '',
      selectedDepartment: '',
      selectedOffice: '',
      viewMode: 'grid',
      selectedEngineer: null,
      isSidebarOpen: false,
      engineerDetails: null,
      engineerLoading: false,
    }
  },
  computed: {
    ...mapGetters('generalData/departments', {
      storeDepartments: 'departments',
    }),
    ...mapGetters('generalData/offices', {
      storeOffices: 'offices',
    }),
    // Roles allowed on the "Engineer Setup" menu item (see navigation/vertical/dashboard.js).
    // These roles see all 3 actions (View, Certificates, Training), regardless of permissions.
    // Anyone else who only has the "training" permission gets View + Training only.
    canViewCertificates() {
      const fullAccessRoles = [
        'admin',
        'manager',
        'process-manager',
        'hr',
        'hr-office',
        'super-manager',
      ]
      const role = (localStorage.getItem('accessRole') || '').trim().toLowerCase()
      if (fullAccessRoles.includes(role)) return true

      const permissions = (localStorage.getItem('permissions') || '')
        .split(',')
        .map(p => p.trim().toLowerCase())
      return !permissions.includes('training')
    },
    departmentsList() {
      return this.storeDepartments || []
    },
    officesList() {
      return (this.storeOffices || []).filter(off => off.value != null && off.value !== '')
    },
    uniqueDepartments() {
      return this.departmentsList.length
    },
    uniqueOffices() {
      return this.officesList.length
    },
    totalActiveLoad() {
      let total = 0
      this.rows.forEach(r => {
        const match = (r.name || '').match(/\(Load\s*(\d+)\)/i)
        if (match) total += parseInt(match[1], 10)
      })
      return total
    },
  },
  async mounted() {
    // The filter lists are optional: if one fails (e.g. 403 Forbidden on departments),
    // the engineers list must still load, so swallow the error here.
    await Promise.all([
      this.$store.dispatch('generalData/departments/fetchDepartments').catch(() => {}),
      this.$store.dispatch('generalData/offices/fetchOffices').catch(() => {}),
    ])

    const q = this.$route.query
    if (q.search) this.searchTerm = q.search
    if (q.department_id) this.selectedDepartment = q.department_id
    if (q.office_id) this.selectedOffice = q.office_id
    if (q.page) this.page = parseInt(q.page, 10) || 1
    this.getAllData(this.page)
  },
  methods: {
    syncFiltersToUrl() {
      const query = {}
      if (this.searchTerm) query.search = this.searchTerm
      if (this.selectedDepartment) query.department_id = String(this.selectedDepartment)
      if (this.selectedOffice) query.office_id = String(this.selectedOffice)
      if (this.page > 1) query.page = String(this.page)
      const isSame = JSON.stringify(this.$route.query) === JSON.stringify(query)
      if (!isSame) {
        this.$router.replace({ query })
      }
    },
    async getAllData(page = 1) {
      this.page = page
      this.syncFiltersToUrl()
      this.dataLoad = true
      try {
        const params = {
          skip: (page - 1) * this.perPage,
        }
        if (this.searchTerm) params.search = this.searchTerm
        if (this.selectedDepartment) params.department_id = this.selectedDepartment
        if (this.selectedOffice) params.office_id = this.selectedOffice

        const response = await axios.get('engineer', { params })
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data || {}
          const listKey = Object.keys(data).find(key => Array.isArray(data[key]))
          this.rows = listKey ? data[listKey] : []
          this.totalRows = data.count || this.rows.length || 0
        }
      } catch (e) {
        // silent
      } finally {
        this.dataLoad = false
      }
    },
    onFilterChange() {
      this.getAllData(1)
    },
    searchNow() {
      this.getAllData(1)
    },
    clearSearch() {
      this.searchTerm = ''
      this.getAllData(1)
    },
    getNameWithoutLoad(name) {
      if (!name) return ''
      return name.replace(/\s*\(Load\s*\d+\)/i, '').trim()
    },
    getLoadFromName(name) {
      if (!name) return null
      const match = name.match(/\(Load\s*(\d+)\)/i)
      return match ? match[1] : null
    },
    getInitials(name) {
      const clean = this.getNameWithoutLoad(name)
      if (!clean) return '?'
      const parts = clean.split(' ').filter(Boolean)
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },
    formatLoad(load) {
      if (!load) return []
      return load.split('-').map(item => {
        const match = item.trim().match(/(\d+)\s*\((.*?)\)/)
        return match
          ? { count: match[1], label: match[2] }
          : null
      }).filter(Boolean)
    },
    loadPercent(count) {
      const num = parseInt(count, 10) || 0
      const max = 100
      return Math.min(100, (num / max) * 100)
    },
    loadFillClass(count) {
      const num = parseInt(count, 10) || 0
      if (num === 0) return 'load-empty'
      if (num < 20) return 'load-low'
      if (num < 50) return 'load-mid'
      return 'load-high'
    },
    loadAccentClass(engineer) {
      const match = (engineer.name || '').match(/\(Load\s*(\d+)\)/i)
      const num = match ? parseInt(match[1], 10) : 0
      if (num === 0) return 'accent-empty'
      if (num < 20) return 'accent-low'
      if (num < 50) return 'accent-mid'
      return 'accent-high'
    },
    formatDate(d) {
      if (!d) return '—'
      try {
        const date = new Date(d)
        return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
      } catch (e) {
        return d
      }
    },
    onImgError(e) {
      e.target.style.display = 'none'
    },
    goToTraining(id) {
      this.$router.push({ name: 'show-user-training', params: { id } })
    },
    goToCertificates(id) {
      this.$router.push({ name: 'certificates-status-work', params: { id } })
    },
    handleRowClick(item) {
      this.selectedEngineer = item
      this.isSidebarOpen = true
      this.fetchEngineerDetails(item.id)
    },
    handleSidebarClose() {
      this.isSidebarOpen = false
      this.selectedEngineer = null
      this.engineerDetails = null
      this.engineerLoading = false
    },
    async fetchEngineerDetails(id) {
      if (!id) return
      this.engineerLoading = true
      try {
        const res = await axios.get(`/engineer/${id}`)
        this.engineerDetails = decryptData(res.data.payload).data.engineer
      } catch (e) {
        // silent
      } finally {
        this.engineerLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped src="./Engineers.styles.scss"></style>
<style lang="scss" src="./Engineers.pagination.scss"></style>
